// Generated by GitHub Copilot
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import type Team from '#core/entities/operations/teams/Team.ts'
import type UserCompany from '#core/entities/auth/users/UserCompany.ts'
import { SettingsTable } from './settings-table.tsx'

interface TeamsTabProps {
  teams: Team[]
  totalCount: number
  users: UserCompany[]
  onEdit: (teamId: string) => void
  onDelete: (teamId: string) => void
}

/** Teams table with subbar showing count. */
export function TeamsTab({ teams, totalCount, users, onEdit, onDelete }: TeamsTabProps) {
  const { t } = useTranslation('settings')

  const userMap = new Map(users.map(u => [u.id, u]))

  function resolveName(userId: string): string {
    return userMap.get(userId)?.displayName ?? userId
  }

  return (
    <SettingsTable
      isEmpty={teams.length === 0}
      emptyLabel={t('usersTeams.noTeams')}
      countLabel={t('usersTeams.teamsShowing', { count: teams.length, total: totalCount })}
      headers={
        <>
          <th>{t('usersTeams.teamTable.name')}</th>
          <th>{t('shared.fields.description')}</th>
          <th>{t('shared.fields.teamLead')}</th>
          <th>{t('shared.fields.teamMembers')}</th>
          <th className="text-end">{t('shared.fields.actions')}</th>
        </>
      }
    >
      {teams.map(team => {
        const lead = team.members.find(m => m.role === 'lead')
        const memberNames = team.members
          .filter(m => m.role !== 'lead')
          .map(m => resolveName(m.user_id))
          .join(', ')

        return (
          <tr key={team.id}>
            <td className="fw-medium">{team.name}</td>
            <td className="text-secondary text-truncate" style={{ maxWidth: '16rem' }}>
              {team.description || '\u2014'}
            </td>
            <td>{lead ? resolveName(lead.user_id) : '\u2014'}</td>
            <td className="text-secondary">{memberNames || '\u2014'}</td>
            <td className="text-end">
              <button type="button" className="btn btn-sm btn-icon p-1" title={t('shared.actions.edit')} onClick={() => onEdit(team.id)}>
                <Pencil size={16} className="text-secondary" />
              </button>
              <button type="button" className="btn btn-sm btn-icon p-1 ms-1" title={t('shared.actions.delete')} onClick={() => onDelete(team.id)}>
                <Trash2 size={16} className="text-danger" />
              </button>
            </td>
          </tr>
        )
      })}
    </SettingsTable>
  )
}
